import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TogoService } from '../../../services/togo.service';
import { FNFResponse } from '../../../models/response';
import jsPDF from 'jspdf';
import { SettingService } from '../../../services/setting.service';
import { RestaurantService } from '../../../services/restaurant.service';
import { Router } from '@angular/router';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { isPosNotSelected } from '../../../utils/pos-provider.util';

@Component({
  selector: 'app-togo-setting',
  standalone: true,
  imports: [],
  templateUrl: './togo-setting.component.html',
  styleUrl: './togo-setting.component.css',
  animations: [
    // "Require Table Number" drops down out of the To Go Dining row when dining is
    // switched on, and folds back up into it when switched off.
    trigger('dropFromDining', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        query('.setting-row-sub', style({ transform: 'translateY(-14px) scale(0.98)' })),
        group([
          animate('320ms cubic-bezier(0.2, 0.8, 0.2, 1)', style({ height: '*' })),
          animate('240ms 60ms ease-out', style({ opacity: 1 })),
          query(
            '.setting-row-sub',
            animate('360ms cubic-bezier(0.34, 1.4, 0.64, 1)', style({ transform: 'none' })),
          ),
        ]),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1 }),
        group([
          query(
            '.setting-row-sub',
            animate(
              '200ms cubic-bezier(0.4, 0, 1, 1)',
              style({ transform: 'translateY(-14px) scale(0.98)' }),
            ),
          ),
          animate('160ms ease-in', style({ opacity: 0 })),
          animate('260ms 60ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: 0 })),
        ]),
      ]),
    ]),
  ],
})
export class TogoSettingComponent {
  ngxSpinnerService = inject(NgxSpinnerService);
  toastr = inject(ToastrService);
  togoService = inject(TogoService);
  togos: any = [];
  @ViewChild('qrCodeImage') qrCodeImage!: ElementRef;
  settingService = inject(SettingService);
  currentRestaurantSettings: any;
  isCheckedTogoFeature: boolean = false;
  displayLoading: boolean = false;
  isCheckedTogoDiningFeature: boolean = false;
  /** Order switches waiting for the server, keyed by order type / setting key. */
  saving: Record<string, boolean> = {};
  /** Off until the first settings arrive, so the row doesn't animate on page load. */
  subSettingAnimates = false;
  prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  isTableNumberRequired: boolean = false;
  restaurantPosProvider: string = 'fnf';
  restaurantService = inject(RestaurantService);
  router = inject(Router);
  displayQrCodePath: string = '';
  enablingFnf = false;

  /** No POS chosen yet (`none`): ordering is off, so there is no QR to show —
   * the page offers Enable Fork & Find instead of an empty QR. */
  get isPosNotSelected(): boolean {
    return isPosNotSelected(this.restaurantPosProvider);
  }

  constructor() {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
      this.displayQrCodePath = this.findQrCodePath();
    });
  }

  private findQrCodePath(): string {
    return (
      (this.togos || []).find((togo: any) => togo.pos_provider === this.restaurantPosProvider)
        ?.qr_code_path || ''
    );
  }

  enableFnf() {
    this.enablingFnf = true;
    this.restaurantService
      .enableForkAndFind(this.restaurantService.currentRestaurantDetails$.value)
      .subscribe({
        next: (response: FNFResponse<any>) => {
          this.enablingFnf = false;
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            return;
          }
          this.toastr.success(
            'Fork & Find enabled. Our support team will activate your restaurant within 1-2 working days.',
            'Success',
          );
        },
        error: () => {
          this.enablingFnf = false;
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        },
      });
  }

  ngOnInit() {
    this.getTogos();
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      if (flags && !isFeatureActive(flags, FEATURE_FLAG_KEYS.TO_GO)) {
        this.router.navigate(['/']);
        return;
      }
    });
    this.settingService.currentRestaurantSettings$.subscribe((settings: any) => {
      this.currentRestaurantSettings = settings;
      this.isCheckedTogoFeature =
        this.currentRestaurantSettings?.find((type: any) => type.setting_type === 'order')
          ?.settings_config?.togo?.enabled || false;
      this.isCheckedTogoDiningFeature =
        this.currentRestaurantSettings?.find((type: any) => type.setting_type === 'order')
          ?.settings_config?.togo_dine_in?.enabled || false;
      if (!this.subSettingAnimates) setTimeout(() => (this.subSettingAnimates = true));
      this.isTableNumberRequired =
        this.currentRestaurantSettings?.find((type: any) => type.setting_type === 'order')
          ?.settings_config?.togo_dine_in?.table_number_required || false;
    });
  }

  getTogos() {
    this.displayLoading = true;
    this.togoService.getTogos().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.togos = response.data;
        this.displayQrCodePath = this.findQrCodePath();
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  downloadQR(togo: any) {
    const doc = new jsPDF();
    doc.addImage(this.displayQrCodePath, 'PNG', 10, 10, 100, 100);
    doc.save('ToGo-QR-Code' + '.pdf');
  }

  downloadAsPNG(togo: any) {
    const imgElement: HTMLImageElement = this.qrCodeImage.nativeElement;
    // const imageUrl = imgElement.src;

    // Fetch the image data
    fetch(this.displayQrCodePath, {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache' },
    })
      .then((response) => response.blob())
      .then((blob) => {
        // Create a URL for the Blob and download it
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'ToGo-QR-Code' + '.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((error) => {
        console.error('Error downloading the image:', error);
      });
  }

  printQR(togo: any) {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      let src = this.displayQrCodePath;
      printWindow.document.write(`
            <html>
                <head>
                <style>
                    /* Center the image and text on the page */
                    body {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100vh;
                    margin: 0;
                    font-family: Arial, sans-serif;
                    }
                    img {
                    max-width: 100%;
                    max-height: 100%;
                    border: 1px solid #ccc;
                    padding: 10px;
                    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
                    }
                    p {
                    margin-top: 20px;
                    font-size: 36px;
                    font-weight: bold;
                    color: #333;
                    }
                </style>
                </head>
                <body>
                <img src="${src}" alt="QR Code" />
                <p>ToGo QR Code</p>
                </body>
            </html>
            `);

      // Automatically close the print window after printing
      printWindow.onafterprint = () => {
        printWindow.close();
      };

      // Trigger the print dialog
      printWindow.print();
    }
  }

  toggleTogoFeature(event: any, isGenerated: boolean = false) {
    const input = event.target as HTMLInputElement;
    const value = isGenerated ? true : input.checked;
    const previous = this.isCheckedTogoFeature;
    // Flip at once; the switch is disabled while saving and flips back if it fails.
    this.isCheckedTogoFeature = value;
    this.saveOrderSwitch(
      'togo',
      { order_type: 'togo', value },
      'Togo feature toggle updated',
      (order) => (order.settings_config.togo.enabled = value),
      () => {
        this.isCheckedTogoFeature = previous;
        if (!isGenerated) input.checked = previous;
      },
    );
  }
  generateTogoFeature(event: any) {
    this.ngxSpinnerService.show();
    this.togoService.createTogo({}).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('ToGo feature generated successfully', 'Success');
          this.getTogos();
          this.isCheckedTogoFeature = true;
          this.toggleTogoFeature(event, true);
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  toggleTogoDiningFeature(event: any) {
    const input = event.target as HTMLInputElement;
    const value = input.checked;
    const previous = this.isCheckedTogoDiningFeature;
    // Flip at once so Require Table Number drops down (or folds up) with the click.
    this.isCheckedTogoDiningFeature = value;
    this.saveOrderSwitch(
      'togo_dine_in',
      { order_type: 'togo_dine_in', value },
      'Togo dining feature toggle updated',
      (order) => (order.settings_config.togo_dine_in.enabled = value),
      () => {
        this.isCheckedTogoDiningFeature = previous;
        input.checked = previous;
      },
    );
  }

  toggleTableNumberRequired(event: any) {
    const input = event.target as HTMLInputElement;
    const value = input.checked;
    const previous = this.isTableNumberRequired;
    this.isTableNumberRequired = value;
    this.saveOrderSwitch(
      'table_number_required',
      { order_type: 'togo_dine_in', setting_key: 'table_number_required', value },
      'Table number setting updated',
      (order) =>
        (order.settings_config.togo_dine_in = {
          ...order.settings_config.togo_dine_in,
          table_number_required: value,
        }),
      () => {
        this.isTableNumberRequired = previous;
        input.checked = previous;
      },
    );
  }

  /**
   * Saves one order switch without the full-screen spinner (its grey backdrop flashed over
   * the page on every toggle). [saving] disables that switch until the answer arrives;
   * [onFailed] puts it back.
   */
  private saveOrderSwitch(
    key: string,
    body: Record<string, unknown>,
    successMessage: string,
    applyToOrderSettings: (order: any) => void,
    onFailed: () => void,
  ) {
    this.saving[key] = true;
    this.settingService.editSettings(body).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data) {
          this.toastr.error(
            response.error
              ? response.error.error?.message || response.error.message
              : 'No data found!',
            'Error',
          );
          onFailed();
        } else {
          this.toastr.success(successMessage, 'Success');
          this.currentRestaurantSettings.forEach((type: any) => {
            if (type.setting_type === 'order') applyToOrderSettings(type);
          });
          this.settingService.currentRestaurantSettings$.next(this.currentRestaurantSettings);
        }
        this.saving[key] = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        onFailed();
        this.saving[key] = false;
      },
    });
  }
}
